import React, { useEffect, useMemo, useState } from 'react';
import {
  deleteCountryQr,
  getSiteSettings,
  saveCountryPaymentModes,
  uploadCountryGenericQr,
  uploadCountrySpecifiedQr,
} from '../lib/adminApi';
import { formatSgd, SGD_DAY_RATES } from '../lib/pricing';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const MODE_OPTIONS = [
  { value: 'PSP', label: 'PSP' },
  { value: 'generic', label: 'Generic QR payments' },
  { value: 'specified', label: 'Specified QR payments' },
];

export default function QrPayment() {
  const [countryModes, setCountryModes] = useState({});
  const [qrByCountry, setQrByCountry] = useState({});
  const [rates, setRates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState('');
  const [filter, setFilter] = useState('');
  const [uploadCountry, setUploadCountry] = useState('Singapore');

  const applyPayload = (data) => {
    setCountryModes(data.countryModes || {});
    setQrByCountry(data.qrByCountry || {});
    setRates(data.rates?.length ? data.rates : SGD_DAY_RATES);
    setCountries(data.countries || []);
  };

  const load = () => {
    getSiteSettings()
      .then(applyPayload)
      .catch((err) => setError(err.message));
  };

  useEffect(load, []);

  const setMode = async (country, mode) => {
    const next = { ...countryModes, [country]: mode };
    setCountryModes(next);
    setSaving(`mode:${country}`);
    setError('');
    try {
      applyPayload(await saveCountryPaymentModes(next));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const countryQr = qrByCountry[uploadCountry] || { generic: '', specified: {} };

  const handleGenericFile = async (file) => {
    if (!file) return;
    setSaving('generic');
    setError('');
    try {
      applyPayload(await uploadCountryGenericQr(uploadCountry, await fileToDataUrl(file)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const handleDayFile = async (days, file) => {
    if (!file) return;
    setSaving(`day:${days}`);
    setError('');
    try {
      applyPayload(await uploadCountrySpecifiedQr(uploadCountry, days, await fileToDataUrl(file)));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const handleDeleteGeneric = async () => {
    setSaving('generic');
    setError('');
    try {
      applyPayload(await deleteCountryQr(uploadCountry, { generic: true }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const handleDeleteDay = async (days) => {
    setSaving(`day:${days}`);
    setError('');
    try {
      applyPayload(await deleteCountryQr(uploadCountry, { days }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving('');
    }
  };

  const dayRows = useMemo(() => {
    const rows = [];
    for (let d = 1; d <= 50; d++) {
      const fee = rates[d];
      if (fee == null) continue;
      rows.push({ days: d, fee, url: countryQr.specified?.[String(d)] || '' });
    }
    return rows;
  }, [rates, countryQr]);

  const visibleCountries = countries.filter((c) =>
    !filter || c.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>QR Payment</h1>
          <p>
            Tag each country as PSP, Generic QR payments, or Specified QR payments.
            PSP uses a future payment-provider script when one exists; otherwise checkout
            uses that country’s uploaded QR (specified first, then generic), then Singapore.
            Singapore never shows the PayNow fallback apology.
          </p>
        </div>
      </div>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      <div className="admin-card" style={{ marginBottom: 20 }}>
        <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Country payment mode</h2>
        <input
          className="admin-input"
          placeholder="Filter countries…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ maxWidth: 280, marginBottom: 12 }}
        />
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Mode</th>
                <th>PSP script</th>
                <th>Payment fallback method</th>
              </tr>
            </thead>
            <tbody>
              {visibleCountries.map((c) => (
                <tr key={c.iso2}>
                  <td>{c.name}</td>
                  <td>
                    <select
                      value={countryModes[c.name] || (c.name === 'Singapore' ? 'specified' : 'PSP')}
                      onChange={(e) => setMode(c.name, e.target.value)}
                      disabled={Boolean(saving)}
                    >
                      {MODE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td>{c.hasPspScript ? 'Ready' : 'Not built — QR fallback'}</td>
                  <td>{c.fallbackMethod || 'None'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-card">
        <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>QR codes by country</h2>
        <p className="admin-hint">
          Choose a country, then upload a generic QR (customer enters the amount) and/or a specified QR for each
          payment schedule. Specified QRs are used first when both exist.
        </p>
        <div className="admin-field" style={{ maxWidth: 360, marginBottom: 18 }}>
          <label htmlFor="qr-upload-country">Country</label>
          <select
            id="qr-upload-country"
            className="admin-input"
            value={uploadCountry}
            onChange={(e) => setUploadCountry(e.target.value)}
          >
            {countries.map((c) => (
              <option key={c.iso2} value={c.name}>{c.name}</option>
            ))}
          </select>
        </div>

        <h3 style={{ fontSize: '0.98rem', margin: '0 0 8px' }}>Generic QR payments</h3>
        <p className="admin-hint">Generic QR code — no preset amount</p>
        <table className="admin-table" style={{ marginBottom: 24 }}>
          <thead>
            <tr>
              <th>QR</th>
              <th>Upload</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span>Generic QR code — no preset amount</span>
              </td>
              <td>
                <input
                  type="file"
                  accept="image/*"
                  disabled={saving === 'generic'}
                  onChange={(e) => handleGenericFile(e.target.files?.[0])}
                />
              </td>
              <td>
                {countryQr.generic ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    disabled={saving === 'generic'}
                    onClick={handleDeleteGeneric}
                  >
                    Delete
                  </button>
                ) : null}
              </td>
            </tr>
          </tbody>
        </table>

        <h3 style={{ fontSize: '0.98rem', margin: '0 0 8px' }}>Specified QR payments</h3>
        <p className="admin-hint">
          Upload a PayNow QR for each duration for {uploadCountry}. The public payment page uses the QR tagged to
          the booked number of days (for example 5 days · SGD 8.00).
        </p>
        <div className="admin-table-wrap" style={{ maxHeight: 520, overflow: 'auto' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Schedule</th>
                <th>QR</th>
                <th>Upload</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {dayRows.map((row) => (
                <tr key={row.days}>
                  <td>{row.days} day{row.days === 1 ? '' : 's'} — {formatSgd(row.fee)}</td>
                  <td>
                    {row.url ? (
                      <img src={row.url} alt={`${row.days} day QR`} style={{ width: 72, height: 72, objectFit: 'contain', borderRadius: 8 }} />
                    ) : (
                      <span className="admin-hint">None</span>
                    )}
                  </td>
                  <td>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={saving === `day:${row.days}`}
                      onChange={(e) => handleDayFile(row.days, e.target.files?.[0])}
                    />
                  </td>
                  <td>
                    {row.url ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        disabled={saving === `day:${row.days}`}
                        onClick={() => handleDeleteDay(row.days)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
